const API_BASE_URL = '/api/v1/dvr-service'

/**
 * The request had no timeout. When the service stalled, the promise never
 * settled: the welcome screen's loader span forever with no error and no way
 * back, and the same URL worked on a retry. Generous enough not to fail a slow
 * but working lookup — the point is that a stall now ends in an error the user
 * can act on rather than an indefinite spinner.
 */
const REQUEST_TIMEOUT_MS = 30000

/**
 * Thrown for any non-2xx from dvr-service. Exported because the status is the
 * only thing that separates "we could not read this particular video" (400 /
 * 404) from "the lookup itself broke" (502 / 503 / 504) — a distinction the
 * user-facing error depends on, see validate-video.ts.
 */
export class ResponseError extends Error {
    response: Response
    fieldErrors: unknown

    constructor(response: Response, fieldErrors: unknown) {
        super(response.statusText)
        this.name = 'ResponseError'
        this.response = response
        this.fieldErrors = fieldErrors
    }
}

const checkStatus = (res: Response) => {
    const { status, ok } = res
    if (status === 204) {
        return ok
    }
    if (status >= 200 && status < 300) {
        return res
    }

    return res.json().then((fieldErrors) => {
        throw new ResponseError(res, fieldErrors)
    })
}

/**
 * Fetches embed metadata for a YouTube or Vimeo url.
 * Extracted from the legacy supercut API class (components/supercut/shared/utils/api.ts:156)
 * plus getResults (shared/api/index.ts:84) so the shared core does not depend on
 * supercut or on legacy's `shared/*` webpack aliases. Request and response
 * handling are unchanged.
 */
const getVideoEmbedInfo = (videoUrl: string, withCaption: boolean, quality = 'hd'): Promise<any> => {
    const url = `${API_BASE_URL}/video_info/?url=${encodeURIComponent(
        videoUrl
    )}&withCaption=${withCaption}&quality=${quality}`

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)

    return fetch(url, {
        method: 'GET',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        signal: controller.signal,
    })
        .finally(() => clearTimeout(timeout))
        .then(checkStatus)
        .then((response: any) => (response.json ? response.json() : response))
        .then(({ status, result, ...whatever }: any) => {
            if (!result) {
                // just return the whole response object
                return whatever
            }
            // some responses have a nested "result"
            return { status, ...result }
        })
}

export default getVideoEmbedInfo
